import React, { useState, useMemo } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Box,
    TextField,
    Autocomplete,
    Typography,
    TablePagination,
    Paper,
    Chip,
    Button,
    ToggleButton,
    ToggleButtonGroup
} from "@mui/material";

import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import { autoTable } from "jspdf-autotable";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSort, faSortUp, faSortDown, faDownload, faArrowUp, faArrowDown } from "@fortawesome/free-solid-svg-icons";
import { format } from "date-fns";

export default function MUIDataTable({
    data = [],
    dataColumns = [],
    companyName = "",
    reportTitle = "report",
    orderGroup = "none",
    enableCurrency = false,
    filterDate = {},
    tradeLabel = "All",
    hideIfZeroKeys = [],
    exportColumnKeys = [],
    enableHeaderFilter = false

}) {

    /* ================= STATE ================= */

    const [filters, setFilters] = useState({});
    const [sortConfig, setSortConfig] = useState({ key: null, dir: "asc" });
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [currency, setCurrency] = useState('all');

    const colType = ['number', 'currency']
    /* ================= COLUMN FILTER ================= */

    const columns = useMemo(() => {
        return dataColumns.filter(col => {
            if (currency === "rupees" && ["AmountD", "NETAMTD", "S_Rate", "BrokAmtD", "AftBrokNetAmtD"].includes(col.key)) return false;
            if (currency === "dollar" && ["Amount", "NetAmtRs", "Rate", "BrokAmtRs", "AftBrokNetAmtRs"].includes(col.key)) return false;
            return true;
        });
    }, [dataColumns, currency]);

    const formatDateForCompare = (value) => {
        if (!value) return "";
        const d = new Date(value);
        return isNaN(d) ? "" : format(d, "dd/MM/yyyy");
    };

    const formatNumberForCompare = (value) => {
        return Number(value || 0).toLocaleString("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };
    const getUniqueValues = (col) =>
        [...new Set(data.map(item => item[col.key]))]
            .filter(v => v !== undefined && v !== null)
            .map(v =>
                col.type == "number"
                    ? formatValue(v, "number")
                    : col.type == "date"
                        ? formatValue(v, "date") : v
            );

    /* ================= SORT ================= */

    const handleSort = (col) => {
        setSortConfig((prev) => {
            if (prev.key !== col.key) {
                // New column: start with ascending
                return { key: col.key, direction: "asc" };
            }
            // Cycle asc → desc → default
            if (prev.direction === "asc") return { key: col.key, direction: "desc" };
            if (prev.direction === "desc") return { key: null, direction: null }; // default
            return { key: col.key, direction: "asc" };
        });
    };

    const filteredData = useMemo(() => {
        return data.filter(row =>
            Object.entries(filters).every(([key, selected]) => {
                if (!selected?.length) return true;

                return selected.some(selValue => {
                    const rowValue = row[key];

                    // DATE
                    if (columns.find(c => c.key === key)?.type === "date") {
                        const d = new Date(rowValue);
                        const formatted = isNaN(d) ? "" : format(d, "dd/MM/yyyy");
                        return formatted === selValue;
                    }

                    // NUMBER
                    if (columns.find(c => c.key === key)?.type === "number") {
                        return formatNumberForCompare(rowValue) === selValue;
                    }

                    // STRING
                    return String(rowValue ?? "") === String(selValue);
                });
            })
        );
    }, [data, filters, columns]);

    console.log(filters, "fil")
    const sortedData = useMemo(() => {
        if (!sortConfig.key || !sortConfig.direction) return filteredData; // default order

        const colType = columns.find((c) => c.accessor === sortConfig.key)?.type;

        return [...filteredData].sort((a, b) => {
            let aVal = a[sortConfig.key];
            let bVal = b[sortConfig.key];

            if (colType === "number") {
                aVal = Number(aVal) || 0;
                bVal = Number(bVal) || 0;
            } else if (colType === "date") {
                aVal = new Date(aVal).getTime() || 0;
                bVal = new Date(bVal).getTime() || 0;
            } else {
                aVal = aVal?.toString().toLowerCase() || "";
                bVal = bVal?.toString().toLowerCase() || "";
            }

            if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
            if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
            return 0;
        });
    }, [filteredData, sortConfig, columns]);

    /* ================= FILTER ================= */


    const getLabelByKey = (key) => {
        const col = dataColumns.find(c => c.key === key);
        return col ? col.label : key;
    };

    const handleRemoveTag = (field, value) => {
        const updated = filters[field].filter((v) => v !== value);
        setFilters(prev => ({ ...prev, [field]: updated }));

        // ⬆️ same function Autocomplete uses to update states
    };

    /* ================= FORMAT VALUE ================= */

    const formatValue = (value, type) => {

        if (type === "number") {
            return Number(value || 0).toLocaleString("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            });
        }

        if (type === "date") {
            if (!value) return "";
            const d = new Date(value);
            return isNaN(d) ? "" : format(d, "dd/MM/yyyy");
        }

        return value ?? "";
    };

    /* ================= PAGINATION ================= */

    const paginatedData = useMemo(() => {
        const start = page * rowsPerPage;
        return sortedData.slice(start, start + rowsPerPage);
    }, [sortedData, page, rowsPerPage]);

    /* ================= EXPORT EXCEL ================= */

    const exportToExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("Report");

        const today = new Date().toLocaleDateString("en-GB");
        const totalColumns = columns.length;

        /* ===== Company Name ===== */
        worksheet.mergeCells(1, 1, 1, totalColumns);
        worksheet.getRow(1).getCell(1).value = companyName;
        worksheet.getRow(1).font = { size: 14, bold: true };
        worksheet.getRow(1).alignment = { horizontal: "center" };

        /* ===== Report Title ===== */
        worksheet.mergeCells(2, 1, 2, totalColumns);
        worksheet.getRow(2).getCell(1).value =
            `${reportTitle} - From Date : (${filterDate.fromDate}) To Date : (${filterDate.toDate})`;
        worksheet.getRow(2).font = { bold: true };
        worksheet.getRow(2).alignment = { horizontal: "center" };

        /* ===== Filters Row ===== */
        const filterKeys = Object.keys(filters || {});
        let filterText = "";

        if (filterKeys.length > 0) {
            filterText = filterKeys
                .map(key => {
                    const value = filters[key];
                    if (!value || value.length === 0) return "";
                    return `${key}: ${value.join(", ")}`;
                })
                .filter(Boolean)
                .join(", ");
        }

        // Row 3 → Filters
        worksheet.mergeCells(3, 1, 3, totalColumns);
        const filterRow = worksheet.getRow(3);
        filterRow.getCell(1).value = filterText ? `Filters: ${filterText}` : "";
        filterRow.font = { italic: true, bold: true, color: { argb: "ef8522" } };
        filterRow.alignment = { horizontal: "left", wrapText: true };

        /* ===== Column Headers ===== */
        let headerRowIndex = 4; // shifted because filters added
        const headerRow = worksheet.getRow(headerRowIndex);

        columns.forEach((col, index) => {
            const cell = headerRow.getCell(index + 1);
            cell.value = col.label.toUpperCase().replace("₹", "INR").replace("$", "$");
            cell.font = { bold: true, color: { argb: "FFFFFF" } };
            cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "2980ba" }
            }
            cell.border = { bottom: { style: "thin" } };
            cell.alignment = { horizontal: "center" };
        });

        worksheet.columns = columns.map(() => ({ width: 20 }));

        /* ===== Data Rows ===== */
        const dataStartRow = headerRowIndex + 1;

        filteredData.forEach(row => {
            const rowData = columns.map(col =>
                col.type == "date"
                    ? formatValue(row[col.key], "date")
                    : row[col.key]
            );
            worksheet.addRow(rowData);
        });

        const dataEndRow = worksheet.lastRow.number;

        /* ===== TOTAL Row ===== */
        const totalRow = worksheet.addRow([]);
        //  totalRow.getCell(1).value = "TOTAL";
        // totalRow.getCell(1).font = { bold: true };
        //  cell.font = { bold: true ,color:{argb:"FFFFFF"}};
        columns.forEach((col, index) => {
            const colLetter = worksheet.getColumn(index + 1).letter;

            if (index === 0) {
                totalRow.getCell(index + 1).value = "TOTAL";
                return;
            }

            if (col.key === "PktNo") {
                totalRow.getCell(index + 1).value =
                    `PktNo: ${dataEndRow - dataStartRow + 1}`;
                return;
            }

            // ✅ Weighted
            if (col.weighted) {
                const weightColIndex = columns.findIndex(c => c.key === "Weight");
                const weightColLetter =
                    worksheet.getColumn(weightColIndex + 1).letter;

                totalRow.getCell(index + 1).value = {
                    formula: `"${col.label.toUpperCase().replace("₹", "INR").replace("$", "$")}: " & TEXT(IFERROR(SUMPRODUCT(${colLetter}${dataStartRow}:${colLetter}${dataEndRow},${weightColLetter}${dataStartRow}:${weightColLetter}${dataEndRow})/SUM(${weightColLetter}${dataStartRow}:${weightColLetter}${dataEndRow}),""),"0.00")`
                };

                totalRow.getCell(index + 1).font = { bold: true };
                return;
            }

            // ✅ Normal sum
            if (col.type === "number") {
                totalRow.getCell(index + 1).value = {
                    formula: `"${col.label.toUpperCase().replace("₹", "INR").replace("$", "$")}: " & TEXT(SUM(${colLetter}${dataStartRow}:${colLetter}${dataEndRow}),"0.00")`
                };
                totalRow.getCell(index + 1).font = { bold: true };
            }
        });
        for (let i = 1; i <= columns.length; i++) {
            const cell = totalRow.getCell(i);

            cell.font = { bold: true, color: { argb: "FFFFFF" } };
            cell.alignment = { horizontal: "right" }

            cell.border = {
                top: { style: "thin" },
                bottom: { style: "double" }
            };

            // ✅ Apply background to ALL cells
            cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "2980ba" } // change color if needed
            };
        }

        /* ===== Export ===== */
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(
            new Blob([buffer], {
                type:
                    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            }),
            `${reportTitle}_${format(new Date(), "dd/MM/yyyy")}.xlsx`
        );
    };

    /* ================= EXPORT PDF ================= */

    const exportToPDF = () => {
        const doc = new jsPDF({
            orientation: "landscape",
            unit: "mm"
        });

        doc.setFont("helvetica");
        doc.setFontSize(10);

        const pageWidth = doc.internal.pageSize.getWidth();
        const margin = 10;
        const contentWidth = pageWidth - 2 * margin;

        // ===== HEADER =====
        doc.setFontSize(16);
        doc.setFont("helvetica", "bold");
        //const companyName = compName.length ? compName.join(", ") : "Company";
        const companyNameWidth =
            (doc.getStringUnitWidth(companyName) * doc.internal.getFontSize()) /
            doc.internal.scaleFactor;
        doc.text(
            companyName,
            margin + (contentWidth - companyNameWidth) / 2,
            20
        );

        doc.setFontSize(12);

        const reportTitleWidth =
            (doc.getStringUnitWidth(reportTitle) * doc.internal.getFontSize()) /
            doc.internal.scaleFactor;
        doc.text(
            reportTitle,
            margin + (contentWidth - reportTitleWidth) / 2,
            27
        );

        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        // const { from, to } = getReportDateRange();

        doc.text(`From Date: ${formatValue(filterDate?.fromDate, "date")}`, margin, 40);
        doc.text(`To Date: ${formatValue(filterDate?.toDate, "date")}`, margin + 60, 40);

        doc.line(margin, 44, pageWidth - margin, 44);

        // ===== HELPERS =====
        const toNumber = (val) => {
            if (val === null || val === undefined || val === "") return 0;
            const n = parseFloat(val);
            return isNaN(n) ? 0 : n;
        };

        // ===== COLUMN FILTER =====
        // const hideIfZeroKeys = ["TDSAmt", "DiscAmt", "DiscPer", "GSTAmt"];

        const shouldHideColumn = (key) =>
            hideIfZeroKeys.includes(key) &&
            filteredData.reduce((s, r) => s + toNumber(r[key]), 0) === 0;

        const exportColumn =
            orderGroup === "none"
                ? columns.filter(
                    (col) =>
                        exportColumnKeys.includes(col.key) &&
                        !shouldHideColumn(col.key)
                )
                : columns;


        // ===== TABLE DATA =====
        const tableColumn = exportColumn.map((col) =>
            col.label.replace("₹", "INR").replace("$", "$")
        );

        const formatNumber = (num) => num != null ? Number(num).toLocaleString("en-IN", { minimumFractionDigits: 2 }) : "";


        const tableRows = filteredData.map((row) =>
            exportColumn.map((col) => {
                const value = row[col.key];
                //  console.log(col,row)
                if (col.type == 'date') {
                    return formatValue(row[col.key], "date")
                }
                if (col.type == "number") return formatNumber(value);
                return value ?? '';

            }

            )
        );

        // ===== TOTAL ROW =====
        const totalRow = exportColumn.map((col, idx) => {
            if (idx === 0) return "TOTAL";

            if (col.key === "PktNo") {
                return filteredData.length;
            }


            if (col.weighted) {
                const weightedSum = filteredData.reduce(
                    (s, r) => s + toNumber((r[col.key]) * (toNumber(r.Weight))),
                    0
                );
                const weightSum = filteredData.reduce(
                    (s, r) => s + toNumber(r.Weight),
                    0
                );
                return weightSum ? (weightedSum / weightSum).toFixed(2) : "";


            }



            if (col.type == "number") {
                return filteredData
                    .reduce((s, r) => s + toNumber(r[col.key]), 0)
                    .toFixed(2);
            }

            return "";
        });

        tableRows.push(totalRow);

        // ===== PDF TABLE =====
        autoTable(doc, {
            head: [tableColumn],
            body: tableRows,
            startY: 47,
            margin: { left: margin, right: margin },
            styles: {
                fontSize: 8,
                cellPadding: 2,
                halign: "right",
                valign: "middle"
            },
            headStyles: {
                fillColor: [41, 128, 185],
                textColor: 255,
                fontStyle: "bold",
                halign: "center"
            },
            alternateRowStyles: {
                fillColor: [245, 245, 245]
            },
            theme: "grid",

            didParseCell: function (dataCell) {
                // First column left aligned
                if (dataCell.column.index === 0) {
                    dataCell.cell.styles.halign = 'left';
                }
                if (orderGroup == 'none' && [1, 3, 4, 5].includes(dataCell.column.index)) {
                    dataCell.cell.styles.halign = 'center';
                }

                // Bold and centered for totals row
                if (dataCell.row.index === filteredData.length) {
                    dataCell.cell.styles.fontStyle = 'bold';
                    dataCell.cell.styles.fillColor = [220, 220, 220];
                    dataCell.cell.styles.textColor = [0, 0, 0];
                    if (dataCell.column.index === 0) {
                        dataCell.cell.styles.halign = 'center';
                    }
                }

                // Prevent word breaking in headers
                if (dataCell.row.index === 0) {
                    dataCell.cell.styles.cellPadding = { top: 2, right: 2, bottom: 2, left: 2 };
                }

                // Better handling of long text
                if (typeof dataCell.cell.raw === 'string' && dataCell.cell.raw.length > 15) {
                    dataCell.cell.styles.fontSize = 7;
                }
            },
            willDrawCell: function (dataCell) {
                // Add borders to all cells
                doc.setDrawColor(200, 200, 200);
                doc.setLineWidth(0.1);
                doc.line(
                    dataCell.cell.x,
                    dataCell.cell.y + dataCell.cell.height,
                    dataCell.cell.x + dataCell.cell.width,
                    dataCell.cell.y + dataCell.cell.height
                );
            },
            willDrawPage: function (data) {
                // Add page numbers
                const pageCount = doc.internal.getNumberOfPages();
                doc.setFontSize(8);
                for (let i = 1; i <= pageCount; i++) {
                    doc.setPage(i);
                    doc.text(`Page ${i} of ${pageCount}`, pageWidth - margin, doc.internal.pageSize.getHeight() - 10, { align: 'right' });
                }
            }
        });

        const filename = `${reportTitle}_${format(new Date(), 'dd/MM/yyyy')}.pdf`;
        doc.save(filename);
    };

    /* ================= RENDER ================= */

    return (
        <Paper elevation={2} sx={{ p: 1, borderRadius: 2 }}>
            {filteredData && filteredData?.length > 0 ?
                <Box
                    sx={{
                        width: "100%",
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" }, // ✅ mobile = stack
                        justifyContent: enableCurrency
                            ? { xs: "flex-start", sm: "space-between" }
                            : { xs: "flex-start", sm: "flex-end" },
                        alignItems: { xs: "flex-start", sm: "center" },
                        gap: 1,
                        mb: 1
                    }}
                >

                    {/* LEFT → Currency */}
                    {enableCurrency ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#be079c" }}>
                                CURRENCY:
                            </Typography>

                            <ToggleButtonGroup
                                value={currency}
                                exclusive
                                onChange={(e, val) => val && setCurrency(val)}
                                size="small"
                                color="secondary"
                                sx={{
                                    height: 32,
                                    '& .MuiToggleButton-root': {
                                        px: 4,
                                        py: 1,
                                        fontSize: "0.9rem",
                                        fontWeight: 600,
                                        textTransform: "none"
                                    },
                                    '& .Mui-selected': {
                                        backgroundColor: '#800471',
                                        color: '#fff',
                                        '&:hover': {
                                            backgroundColor: '#840675b1'
                                        }
                                    }
                                }}
                            >
                                <ToggleButton value="all">All</ToggleButton>
                                <ToggleButton value="rupees">₹</ToggleButton>
                                <ToggleButton value="dollar">$</ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                    ) : null}

                    {/* RIGHT → Export Buttons */}
                    <Box
                        sx={{
                            display: "flex",
                            gap: 1,
                            width: { xs: "100%", sm: "auto" },   // ✅ full width on mobile
                            justifyContent: { xs: "flex-start", sm: "flex-end" }, // ✅ align properly
                            mt: { xs: 1, sm: 0 } // ✅ push to new row on mobile
                        }}
                    >
                        <Button
                            size="small"
                            variant="contained"
                            onClick={exportToExcel}
                            sx={{
                                backgroundColor: "#bd0606ff",
                                height: 28,
                                fontSize: "0.7rem",
                                fontWeight: 600,
                                textTransform: "none"
                            }}
                        >
                            <FontAwesomeIcon icon={faDownload} /> &nbsp;Excel
                        </Button>

                        <Button
                            size="small"
                            variant="contained"
                            onClick={exportToPDF}
                            sx={{
                                backgroundColor: "#045f08ff",
                                height: 28,
                                fontSize: "0.7rem",
                                fontWeight: 600,
                                textTransform: "none"
                            }}
                        >
                            <FontAwesomeIcon icon={faDownload} /> &nbsp;PDF
                        </Button>
                    </Box>

                </Box> : ""}


            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', sm: 'center' },
                mb: 0,
                gap: 1,
                p: 1
            }}>
                <Typography variant="h6" sx={{
                    fontWeight: 600,
                    color: 'text.primary',
                    fontSize: "1rem",
                    margin: "0px",
                    padding: "0px"
                }}>
                    {data.length > 0 ?
                        `Showing ${filteredData.length} of ${data.length} records` :
                        'No data available'}
                </Typography>

                {data.length > 0 && (
                    <Box sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1,
                        justifyContent: { xs: 'flex-start', sm: 'flex-end' }
                    }}>
                        {tradeLabel != "" ?
                            <Chip
                                label={`${tradeLabel}`}
                                size="small"
                                color="primary"
                                variant="outlined"
                                sx={{ fontWeight: 500 }}
                            /> : ""}
                        {filterDate && filteredData != "" && Object.keys(filterDate).length > 0 ?
                            <>
                                <Chip
                                    label={`From: ${format(filterDate.fromDate, 'dd-MMM-yyyy')}`}
                                    size="small"
                                    color="secondary"
                                    variant="outlined"
                                    sx={{ fontWeight: 500 }}
                                />
                                <Chip
                                    label={`To: ${format(filterDate.toDate, 'dd-MMM-yyyy')}`}
                                    size="small"
                                    color="secondary"
                                    variant="outlined"
                                    sx={{ fontWeight: 500 }}
                                /></> : ""}
                    </Box>
                )}
            </Box>
            <div style={{ display: "flex", flexDirection: "row", gap: 4, marginBottom: 5 }}>
                {Object.entries(filters).map(([field, values]) => (
                    <div key={field} style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                        <strong style={{ fontSize: "0.6rem" }} className="text-xs uppercase"> {values.length > 0 ? getLabelByKey(field) + " :" : ""} </strong>
                        {values.map(value => (
                            <Chip
                                key={`${field}-${value}`}
                                label={value}
                                onDelete={() => handleRemoveTag(field, value)}
                                size="small"
                                color="secondary"
                                variant="outlined"
                                sx={{ height: 22, fontSize: "11px" }}
                            />
                        ))}
                    </div>
                ))}
            </div>
            <Box sx={{ width: "100%", overflow: "auto", }} className="max-h-[70vh]">
                <Table sx={{ minWidth: "max-content", borderCollapse: "separate", borderSpacing: 0, border: "1px solid #dbdbdbff" }}>
                    <TableHead>
                        <TableRow>
                            {columns.map(col => (
                                <TableCell key={col.key} sx={{
                                    // background: "rgb(3, 95, 165)",
                                    color: "rgb(255, 255, 255)",
                                    fontWeight: "bold",
                                    position: "sticky",
                                    top: 0,

                                    zIndex: 5,
                                    minWidth: 150,
                                    padding: 0
                                    // px: 1.5,
                                    //py: 1,
                                }}>
                                    <div className="bg-sky-700 p-2">
                                        <Box display="flex" alignItems="center" justifyContent="space-evenly" sx={{ cursor: "pointer", textTransform: "uppercase" }} onClick={() => handleSort(col)}>
                                            <div className="text-semibold break-all text-xs">  {col.label}</div>
                                            {sortConfig.key === col.key ? (
                                                sortConfig.direction === "asc" ? (
                                                    <FontAwesomeIcon icon={faArrowUp} style={{ fontSize: "10px" }} />
                                                ) : sortConfig.direction === "desc" ? (
                                                    <FontAwesomeIcon icon={faArrowDown} style={{ fontSize: "10px" }} />
                                                ) : (
                                                    // default order icon
                                                    <FontAwesomeIcon icon={faSort} style={{ fontSize: "10px", color: "gray" }} />
                                                )
                                            ) : (
                                                // unsorted column
                                                <FontAwesomeIcon icon={faSort} style={{ fontSize: "12px", }} className="text-gray-400" />
                                            )}
                                        </Box>
                                    </div>
                                    {enableHeaderFilter ?
                                        <div className="bg-sky-100 p-1 ">
                                            <Autocomplete
                                                multiple
                                                size="small"
                                                options={getUniqueValues(col)}
                                                value={filters[col.key] || []}
                                                onChange={(e, newValue) => {
                                                    setFilters(prev => ({
                                                        ...prev,
                                                        [col.key]: newValue
                                                    }));
                                                    setPage(0);
                                                }}
                                                noOptionsText="No options available"
                                                renderTags={() => null}
                                                slotProps={{
                                                    chip: {
                                                        size: "small",
                                                        sx: { height: 20, fontSize: "10px", padding: "0 4px", "& .MuiChip-label": { padding: 0 } }
                                                    },
                                                    input: { sx: { padding: "2px 0px !important", fontSize: "11px" } },
                                                    popper: { sx: { fontSize: "12px" } },
                                                    listbox: {
                                                        sx: {
                                                            fontSize: "11px",
                                                            "& .MuiAutocomplete-option": { padding: "2px 8px", minHeight: "28px" }
                                                        }
                                                    }
                                                }}
                                                sx={{

                                                    "& .MuiAutocomplete-inputRoot": { padding: "0px 4px !important", minHeight: "30px", background: "#FFFFFF" },
                                                    "& .MuiOutlinedInput-root": { padding: "0px !important", height: "30px" }
                                                }}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label="Find..."
                                                        variant="outlined"
                                                        size="small"
                                                        inputProps={{
                                                            ...params.inputProps, // ⚡ make sure inputProps exist
                                                            autoComplete: "off"
                                                        }}
                                                        sx={{
                                                            //  width: ["S_Name", "BrokerDesc", "PartyName"].includes(col.key) ? "250px" : "140px",
                                                            width: "100%",
                                                            "& .MuiInputLabel-root": { fontSize: "11px", color: "#054195", fontWeight: 600 }
                                                        }}
                                                    />
                                                )}
                                            /></div> : ""}
                                    <div className="border-b-3 border-b-slate-600"></div>
                                </TableCell>
                            ))}
                        </TableRow>

                    </TableHead>

                    <TableBody>
                        {paginatedData.length ? (
                            paginatedData.map((row, idx) => (
                                <TableRow key={idx}
                                    hover
                                    sx={{

                                        "&:nth-of-type(odd)": { background: "#ffffff" },
                                        "&:hover": { background: "#e3f2fd" },
                                    }}>
                                    {columns.map((col) => (
                                        <TableCell key={col.key} sx={{ px: 1.5, py: 0.8, fontSize: "0.8rem", textAlign: col.type == "number" ? "right" : "left" }}>
                                            {col.type == "date"
                                                ? formatValue(row[col.key], "date")
                                                : col.type == "number"
                                                    ? formatValue(row[col.key], "number")//.toFixed(2)
                                                    : row[col.key]}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={columns.length} align="center">
                                    No matching records found
                                </TableCell>
                            </TableRow>
                        )}

                        {/* ===== FOOTER TOTAL ROW ===== */}
                        {filteredData.length > 0 && (
                            <TableRow className="bg-sky-700"
                                sx={{
                                    position: "sticky",
                                    bottom: 0,
                                    // background: "rgb(8, 111, 227)",
                                    zIndex: 10,
                                    padding: 0,
                                }}
                            >
                                {columns.map((col, idx) => {
                                    let colTotal = "";


                                    if (col.weighted) {
                                        const weightSum = filteredData.reduce(
                                            (sum, row) => sum + Number(row.Weight || 0),
                                            0
                                        );

                                        const weightedSum = filteredData.reduce(
                                            (sum, row) =>
                                                sum + (Number(row[col.key] || 0) * Number(row.Weight || 0)),
                                            0
                                        );

                                        colTotal =
                                            weightSum > 0 ? weightedSum / weightSum : 0;
                                    }
                                    else if (col.key === "PktNo") {
                                        colTotal = filteredData.length;
                                    }
                                    else if (
                                        filteredData.length &&
                                        col.type == "number"
                                    ) {
                                        colTotal = filteredData.reduce(
                                            (sum, row) => sum + Number(row[col.key] || 0),
                                            0
                                        );
                                    }

                                    return (
                                        <TableCell key={col.key} sx={{ fontWeight: "bold", padding: 0.5, textAlign: "right", color: "#ffffff" }}>
                                            {idx === 0
                                                ? "Total:"
                                                : colTotal !== ""
                                                    ? formatValue(colTotal, "number")
                                                    : ""}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </Box>

            <TablePagination
                component="div"
                count={filteredData.length}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                showFirstButton={true}
                showLastButton={true}
                onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 75, 100]}
                className="bg-sky-50 shadow  text-red-700 "
                sx={{
                    '.MuiTablePagination-toolbar': {
                        color: '#0b39a5',
                        // Tailwind blue-700 equivalent
                    },
                    '.MuiTablePagination-displayedRows': {
                        color: '#0b39a5',
                        fontWeight: 600
                    },
                    '.MuiTablePagination-selectLabel': {
                        color: '#0b39a5',
                        fontWeight: 600

                    },
                    '.MuiTablePagination-actions button': {
                        color: '#0b39a5',
                        fontWeight: 600// Tailwind red-600
                    },
                    '.MuiTablePagination-actions button:hover': {
                        backgroundColor: '#FEE2E2', // light red on hover
                    }
                }}
            />
        </Paper>
    );
}